import Api from '../api/userApi';

/**
 * Resolve a media URL for use in <img src=...>.
 * - Absolute URLs (http/https) are returned as-is.
 * - Relative upload URLs like "/uploads/..." are prefixed with the backend base (Api.main).
 *
 * Use this for the ORIGINAL, full-resolution image (e.g. fullscreen / lightbox).
 */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/uploads/')) return `${Api.main}${trimmed}`;
  return trimmed;
}

/**
 * Responsive image variants.
 *
 * The backend generates three WebP variants as *sidecar* files next to every
 * uploaded original (e.g. `/uploads/profile/123.png` ->
 * `/uploads/profile/123.thumb.webp`) and serves them through the same static
 * `/uploads` mount as the originals. We therefore address the variants by their
 * real static path rather than a dynamic resolver route, so they load anywhere
 * `/uploads` is served.
 *
 *   thumb     — lists, cards, avatars, small circles
 *   medium    — profile pages, hero foreground
 *   optimized — large responsive candidate (fullscreen still uses original)
 */
export const MEDIA_VARIANTS = ['thumb', 'medium', 'optimized'];

// Client mirror of the server's per-subfolder variant widths
// (see server/utils/imageVariants.js). Used for srcset `w` descriptors.
const VARIANT_WIDTHS = {
  default: { thumb: 200, medium: 640, optimized: 1280 },
  profile: { thumb: 128, medium: 400, optimized: 800 },
  portfolio: { thumb: 320, medium: 800, optimized: 1280 },
  'work-progress': { thumb: 320, medium: 800, optimized: 1280 },
  PopularService: { thumb: 96, medium: 200, optimized: 400 },
  FeaturedService: { thumb: 160, medium: 320, optimized: 480 },
  HomeAssets: { thumb: 640, medium: 1024, optimized: 1920 },
  homeslogonbanner: { thumb: 640, medium: 1024, optimized: 1920 },
};

// Pull "/uploads/<subfolder>/<file>" out of an absolute OR relative URL.
function extractUpload(url) {
  const idx = url.indexOf('/uploads/');
  if (idx === -1) return null;
  const rel = url.slice(idx);
  const m = /^\/uploads\/([^/]+)\/([^/?#]+)/.exec(rel);
  if (!m) return null;
  const [, subfolder, file] = m;
  if (!file || file.includes('..')) return null;
  return { subfolder, file };
}

// Derive the sidecar variant filename for an original file.
// e.g. ("123-456.webp", "optimized") -> "123-456.optimized.webp"
// Mirrors the server's getVariantFileName() so we hit the exact files on disk.
function variantFileName(file, variant) {
  const dot = file.lastIndexOf('.');
  const base = dot === -1 ? file : file.slice(0, dot);
  return `${base}.${variant}.webp`;
}

/**
 * Build the URL for a specific size variant of an uploaded image.
 * Non-upload values (external URLs, data: previews, empty) are returned via
 * resolveMediaUrl unchanged, so this is a safe drop-in for any <img src>.
 *
 * @param {string} url    stored path or absolute URL
 * @param {'thumb'|'medium'|'optimized'} [variant='thumb']
 */
export function mediaVariantUrl(url, variant = 'thumb') {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith('data:')) return trimmed;
  if (!MEDIA_VARIANTS.includes(variant)) return resolveMediaUrl(trimmed);
  const parsed = extractUpload(trimmed);
  if (!parsed) return resolveMediaUrl(trimmed);
  return `${Api.main}/uploads/${parsed.subfolder}/${variantFileName(parsed.file, variant)}`;
}

/**
 * Build a responsive `srcset` string spanning the three variants, using each
 * variant's real pixel width as the `w` descriptor. Returns undefined for
 * non-upload values (so the caller can simply spread it and get nothing).
 *
 * Pair with a `sizes` attribute for correct selection.
 */
export function mediaSrcSet(url) {
  if (!url || typeof url !== 'string') return undefined;
  const parsed = extractUpload(url.trim());
  if (!parsed) return undefined;
  const widths = VARIANT_WIDTHS[parsed.subfolder] || VARIANT_WIDTHS.default;
  return MEDIA_VARIANTS.map(
    (v) => `${Api.main}/uploads/${parsed.subfolder}/${variantFileName(parsed.file, v)} ${widths[v]}w`
  ).join(', ');
}

/**
 * Convenience: returns { src, srcSet } props for a responsive <img>.
 * `src` uses the requested base variant; `srcSet` offers all three.
 */
export function mediaImageProps(url, variant = 'medium') {
  return {
    src: mediaVariantUrl(url, variant),
    srcSet: mediaSrcSet(url),
  };
}
